import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';

/**
 * A choice on a question node. Either leads to another node (`next`) or, when the
 * answer already settles the destination, jumps straight to a form (`route`) with no
 * intermediate outcome page - e.g. "No, just my own party" goes right to the Tourist form.
 */
interface FinderOption {
    label: string;
    next?: string;
    route?: string;
}

/**
 * A node in the finder tree. Either a QUESTION (branches to more nodes) or an
 * OUTCOME (a leaf): a form we can send them to (`route`), or an informational /
 * "TLC-assessed, not online yet" result with no action.
 */
interface FinderNode {
    kind: 'question' | 'outcome';
    /** Overrides the page heading for this step (defaults to "Find the right permit"). */
    pageTitle?: string;
    /** Overrides the page subtitle for this step (e.g. the Recreation eligibility note). */
    intro?: string;
    /** Bullet points shown under the subtitle (e.g. who each Recreation area is open to). */
    introBullets?: string[];
    // question
    question?: string;
    hint?: string;
    options?: FinderOption[];
    // outcome
    title?: string;
    body?: string;
    note?: string;
    route?: string;
    buttonLabel?: string;
}

/**
 * Guided permit finder. Walks the applicant through the §1 finder questions and
 * lands them on the right destination: the automated permits (Tourist Permit,
 * Recreation) route straight to their form; everything else is TLC-assessed and,
 * for now, shows an informational outcome until its form is built.
 *
 * The tree is hardcoded (no time/need for a data-driven engine) but structured,
 * so adding a branch or wiring a new form later is a one-line change.
 */
@Component({
    selector: 'app-permit-finder',
    templateUrl: './permit-finder.component.html',
    styleUrls: ['./permit-finder.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class PermitFinderComponent implements OnInit, OnDestroy {
    public readonly nodes: Record<string, FinderNode> = {
        'tourism': {
            kind: 'question',
            pageTitle: 'Apply for a Tourist Permit',
            question: 'Are you traveling with a tour operator?',
            hint: 'Solo visitors and private parties can apply for a Tourist Permit here. Permits for organised tour groups will be provided for by the tour operator.',
            options: [
                // Straight to the form - a solo/private visit needs no intermediate page.
                { label: 'No - just me or my own party', route: '/apply/tourist-pass' },
                { label: 'Yes - through a tour operator', next: 'tour-operator' },
            ],
        },
        'work-employer': {
            kind: 'question',
            question: 'Does the business you’ll be working for already have a Business Login with TLC?',
            options: [
                { label: 'Yes', next: 'employer-issued' },
                { label: 'No, or I’m not sure', next: 'work-permit' },
            ],
        },
        'fishing-hunting': {
            kind: 'question',
            pageTitle: 'Apply for a Fishing or Hunting Permit',
            question: 'Which do you need?',
            options: [
                { label: 'Fishing', next: 'fishing-eligibility' },
                { label: 'Hunting', next: 'hunting-eligibility' },
            ],
        },

        // Neither permit can be applied for on its own, and the two routes lead to
        // different permits - a visitor gets 14 days under their host, a resident gets
        // an annual one. A yes/no would collapse them and send half the applicants
        // down the wrong path, so this asks which person you are.
        'fishing-eligibility': {
            kind: 'question',
            pageTitle: 'Apply for a Fishing Permit',
            question: 'A fishing permit is always tied to another permit. Which is you?',
            hint: 'Fishing in a Red Zone needs a permit, and permits are granted through the clans whose sea country you are fishing.',
            options: [
                { label: 'I am visiting, and my host holds an annual fishing permit', next: 'fishing-guest' },
                { label: 'I live or work on the islands and hold a work or resident permit', next: 'fishing-annual' },
                { label: 'Neither, or I am not sure', next: 'fishing-ineligible' },
            ],
        },
        'hunting-eligibility': {
            kind: 'question',
            pageTitle: 'Apply for a Hunting Permit',
            question: 'A hunting permit is always tied to another permit. Which is you?',
            hint: 'You will also need a copy of your current shooter\u2019s licence.',
            options: [
                { label: 'I am visiting, and my host holds an annual hunting permit', next: 'hunting-guest' },
                { label: 'I live or work on the islands and hold a work or resident permit', next: 'hunting-annual' },
                { label: 'Neither, or I am not sure', next: 'hunting-ineligible' },
            ],
        },

        // ---- Outcomes: automated permits (route to a form) ----
        'tourist-pass': {
            kind: 'outcome',
            title: 'Tourist Permit',
            body: 'You can apply now. Most tourist permits are issued the moment you submit. If you are going somewhere other than the main townships, TLC check it first.',
            route: '/apply/tourist-pass',
            buttonLabel: 'Continue to Tourist Permit application',
        },
        // Most recreation sites are not open to visitors: "tourists cannot apply. You
        // have to have an eligible existing permit." The exception is boat-access sites,
        // which need no permit number - so this splits three ways rather than gating
        // everyone out. Asking here is the whole point of a finder: the form's permit
        // requirement depends on the site, and being told that after filling it in is
        // the worst place to find out.
        'recreation': {
            kind: 'question',
            pageTitle: 'Apply for a Recreation Permit',
            intro: 'To visit a designated recreation area, you must hold a valid Recreation Permit.',
            introBullets: [
                'Some recreation areas are available only to people who hold current permits to live and/or work on the Tiwi Islands.',
                'Boat access recreation areas are available to everyone.',
            ],
            // No question heading (TLC): the two options are the choice, and the intro
            // bullets above already say who each kind of area is open to - so the old
            // hint here would only repeat them.
            options: [
                { label: 'Apply to visit a recreation area', next: 'recreation-apply' },
                { label: 'Apply to visit a boat access area', next: 'recreation-boat' },
            ],
        },
        'recreation-apply': {
            kind: 'outcome',
            title: 'Recreation Permit',
            body: 'You can apply now. Have your work or long-term resident permit number to hand - you will need to quote it for camping and road sites. Recreation permits are issued once your booking is confirmed.',
            route: '/apply/recreation',
            buttonLabel: 'Continue to Recreation application',
        },
        // Boat-access sites only: the form is narrowed to them (?sites=boat), and it asks
        // for no permit number - so a visitor arriving by boat can apply without one.
        'recreation-boat': {
            kind: 'outcome',
            title: 'Recreation Permit - boat access',
            body: 'You can apply now for a boat-access site. You will not need a work or resident permit number - just your dates, how many people are travelling, and an optional photo of your boat. One permit covers one boat, so if you are bringing more than one boat, apply once for each. Recreation permits are issued once your booking is confirmed.',
            route: '/apply/recreation?sites=boat',
            buttonLabel: 'Continue to Recreation application',
        },
        'recreation-ineligible': {
            kind: 'outcome',
            title: 'You need an eligible permit first',
            body: 'Camping and road recreation sites are only available to people who already hold a current work or long-term resident permit for the Tiwi Islands.',
            // TLC: "For tourists wanting to recreate, they are encouraged to go
            // through an existing tour provider/local business on the Islands." The
            // operator runs the trip on their own arrangements - they do not obtain a
            // recreation permit for the visitor, and saying so would send someone off
            // to ask an operator for something an operator cannot give them. Boat-access
            // sites are the one thing a visitor CAN book directly, so they are named here
            // rather than left to be discovered.
            note: 'Arriving by boat? A boat-access site can be booked without a permit - go back and choose "I am booking a boat-access site". Otherwise, a Tourist Permit covers the main townships, and to camp or fish TLC encourage you to go with a Tiwi tour operator or local business - they run those trips themselves.',
        },

        // ---- Outcomes: handled by a business / operator ----
        'tour-operator': {
            kind: 'outcome',
            title: 'Book through your tour operator',
            body: 'Contact your tour operator directly to apply for your permit.',
        },
        'employer-issued': {
            kind: 'outcome',
            title: 'Ask your employer to issue your permit',
            body: 'The business you’ll be working for has a TLC Business Login and can issue your work permit directly. Please contact them.',
        },

        // ---- Outcomes: TLC-assessed (no online form yet) ----
        'friends-family': {
            kind: 'outcome',
            title: 'Friends & Family Permit',
            body: 'You can apply now. Your host will be contacted to confirm, then TLC reviews your application - it isn’t issued straight away.',
            route: '/apply/friends-family',
            buttonLabel: 'Continue to Friends & Family application',
        },
        'media': {
            kind: 'outcome',
            title: 'Media Permit',
            body: 'You can apply now. Media permits are assessed by TLC’s Media Officer - it isn’t issued straight away.',
            route: '/apply/media',
            buttonLabel: 'Continue to Media application',
        },
        'research': {
            kind: 'outcome',
            title: 'Research Permit',
            body: 'You can apply now. Research permits require a signed Research Access Agreement and are assessed by TLC (Exec Committee + clans) - allow 60+ days.',
            route: '/apply/research',
            buttonLabel: 'Continue to Research application',
        },
        'work-permit': {
            kind: 'outcome',
            title: 'Work Permit',
            body: 'You can apply now. Work permits are reviewed by TLC before they’re approved - it isn’t issued straight away.',
            route: '/apply/work',
            buttonLabel: 'Continue to Work application',
        },
        // `holder` only tailors the wording on the form - the permit number they
        // enter is still what decides the term, because that is checked server-side.
        'fishing-guest': {
            kind: 'outcome',
            title: '14-day Fishing Permit',
            body: 'You can apply now. Have your host\u2019s annual fishing permit number ready - your permit covers the clan areas theirs is approved for. Assessed by TLC.',
            route: '/apply/fishing?holder=host',
            buttonLabel: 'Continue to Fishing application',
        },
        'fishing-annual': {
            kind: 'outcome',
            title: 'Annual Fishing Permit',
            body: 'You can apply now. Have your work or resident permit number ready - it is what makes you eligible. You choose the clan areas you want, and each is consulted separately.',
            route: '/apply/fishing?holder=self',
            buttonLabel: 'Continue to Fishing application',
        },
        'fishing-ineligible': {
            kind: 'outcome',
            title: 'You need an eligible permit first',
            body: 'A fishing permit cannot be applied for on its own. Either your host holds an annual fishing permit you can fish under, or you hold a work or long-term resident permit and can apply for your own.',
            note: 'Visiting for a holiday? A Tourist Permit covers the main townships. To fish, TLC encourage you to go with a Tiwi tour operator or local business - they run those trips themselves. Fishing outside the Red Zones needs no permit at all.',
        },
        'hunting-guest': {
            kind: 'outcome',
            title: '14-day Hunting Permit',
            body: 'You can apply now. Have your host\u2019s annual hunting permit number and your shooter\u2019s licence ready - your permit covers the clan areas theirs is approved for. Assessed by TLC.',
            route: '/apply/hunting?holder=host',
            buttonLabel: 'Continue to Hunting application',
        },
        'hunting-annual': {
            kind: 'outcome',
            title: 'Annual Hunting Permit',
            body: 'You can apply now. Have your work or resident permit number and your shooter\u2019s licence ready. You choose the clan areas you want, and each is consulted separately.',
            route: '/apply/hunting?holder=self',
            buttonLabel: 'Continue to Hunting application',
        },
        'hunting-ineligible': {
            kind: 'outcome',
            title: 'You need an eligible permit first',
            body: 'A hunting permit cannot be applied for on its own. Either your host holds an annual hunting permit you can hunt under, or you hold a work or long-term resident permit and can apply for your own.',
            note: 'Only a non-Tiwi person who lives on the islands is eligible for an annual hunting permit.',
        },
    };

    public currentId = '';
    private sub?: Subscription;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private location: Location,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        // The current step lives in the URL (/find/:step), so the finder is
        // deep-linkable and the browser Back button works. The first choice is not a
        // step here - it is the chooser on /apply, which the home page also shows.
        this.sub = this.route.paramMap.subscribe(params => {
            const step = params.get('step') ?? '';
            if (!this.nodes[step]) {
                // Unknown or stale step id - back to the list of permit types.
                this.router.navigateByUrl('/apply', { replaceUrl: true });
                return;
            }
            this.currentId = step;
            this.cdr.markForCheck();
        });
    }

    public ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    public get node(): FinderNode {
        return this.nodes[this.currentId];
    }

    public get canGoBack(): boolean {
        return true;
    }

    public choose(next: string): void {
        this.router.navigate(['/find', next]);
    }

    /** Follow a question option: jump straight to its form when it has one, else the next node. */
    public select(opt: FinderOption): void {
        if (opt.route) {
            this.go(opt.route);
        } else if (opt.next) {
            this.choose(opt.next);
        }
    }

    public back(): void {
        this.location.back();
    }

    public restart(): void {
        this.router.navigateByUrl('/apply');
    }

    public go(route: string): void {
        this.router.navigateByUrl(route);
    }
}
